import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { getAllMembers } from '../utils/memberLoader';
import { fadeInLeft, fadeInRight, fadeInUp } from '../utils/animations';

function WeeklyHighlight() {
  const [highlightedMember, setHighlightedMember] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const loadHighlight = async () => {
      try {
        const members = await getAllMembers();
        
        if (members.length > 0) {
          // Get the current date
          const now = new Date();
          
          // Calculate which week of the month it is (1-based)
          // Get the day of the month (1-31) and divide by 7, rounding up
          const weekOfMonth = Math.ceil(now.getDate() / 7);
          
          // Calculate index for the member, using modulo to wrap around
          // Subtract 1 from weekOfMonth to get 0-based index
          const memberIndex = (weekOfMonth - 1) % members.length;
          
          setHighlightedMember(members[memberIndex]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading Member Highlight:', error);
        setLoading(false);
      }
    };

    loadHighlight();
  }, []);
  return (
    <>
      <Helmet>
        <title>
          {highlightedMember
            ? `Member Highlight: ${highlightedMember.name} - Red Panda Treehouse`
            : 'Member Highlight - Red Panda Treehouse'}
        </title>
        <meta
          name="description"
          content={
            highlightedMember
              ? `This week's featured talent: ${highlightedMember.name}, ${highlightedMember.title}. ${highlightedMember.description || 'Discover their content and connect with them.'}`
              : 'Featuring our exceptional talent of the week from Red Panda Treehouse community.'
          }
        />
        <meta name="keywords" content="vtuber highlight, featured creator, weekly spotlight, red panda treehouse, vtuber community" />
        <link rel="canonical" href="https://redpandatreehouse.com/highlight" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={
            highlightedMember
              ? `Member Highlight: ${highlightedMember.name}`
              : 'Member Highlight - Red Panda Treehouse'
          }
        />
        <meta
          property="og:description"
          content={
            highlightedMember
              ? `This week's featured talent: ${highlightedMember.name}, ${highlightedMember.title}.`
              : 'Featuring our exceptional talent of the week from Red Panda Treehouse community.'
          }
        />
        <meta property="og:url" content="https://redpandatreehouse.com/highlight" />
        <meta property="og:type" content="website" />
        {highlightedMember && (
          <meta property="og:image" content={`https://redpandatreehouse.com${highlightedMember.image}`} />
        )}

        {/* Twitter */}
        <meta
          name="twitter:title"
          content={
            highlightedMember
              ? `Member Highlight: ${highlightedMember.name}`
              : 'Member Highlight - Red Panda Treehouse'
          }
        />
        <meta
          name="twitter:description"
          content={
            highlightedMember
              ? `This week's featured talent: ${highlightedMember.name}, ${highlightedMember.title}.`
              : 'Featuring our exceptional talent of the week.'
          }
        />
        {highlightedMember && (
          <meta name="twitter:image" content={`https://redpandatreehouse.com${highlightedMember.image}`} />
        )}
      </Helmet>
      <section className='relative bg-gradient-to-r from-secondary-dark to-primary-dark pt-24'>
        <div className='container mx-auto px-4 text-center'>
          <motion.h1 
            className='text-5xl md:text-6xl font-bold mb-4'
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            Member Highlight
          </motion.h1>
          <motion.p 
            className='text-xl opacity-90 max-w-3xl mx-auto'
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            Featuring our exceptional talent of the week
          </motion.p>
        </div>
      </section>

      <section className='py-20'>
        <div className='container mx-auto px-4'>
          {loading ? (
            <div className='bg-white dark:bg-dark-light rounded-xl shadow-xl p-8 text-center'>
              <div className='inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-DEFAULT'></div>
              <p className='mt-4 text-lg'>Loading highlight...</p>
            </div>
          ) : !highlightedMember ? (
            <div className='bg-white dark:bg-dark-light rounded-xl shadow-xl p-8 text-center'>
              <p className='text-xl'>
                No highlighted member available at the moment.
              </p>
              <Link
                to='/members'
                className='inline-block mt-4 bg-primary-DEFAULT hover:bg-primary-dark  font-bold py-2 px-6 rounded-full transition-colors duration-300'
              >
                View All Members
              </Link>
            </div>
          ) : (
            <div className='bg-white dark:bg-dark-light rounded-xl shadow-xl p-8 animate-fade-in'>
              <div className='flex flex-col md:flex-row items-start gap-8'>
                <motion.div 
                  className='md:w-1/2'
                  initial="hidden"
                  animate="visible"
                  variants={fadeInLeft}
                >
                  <img
                    src={highlightedMember.image}
                    alt={highlightedMember.name}
                    className='rounded-lg w-full shadow-xl'
                    onError={(e) => {
                      e.target.src =
                        'https://via.placeholder.com/600x800?text=No+Image';
                    }}
                  />
                  <div className='mt-6 flex justify-center'>
                    <Link
                      to={`/members/${highlightedMember.id}`}
                      className='bg-primary-DEFAULT hover:bg-primary-dark font-bold py-2 px-6 rounded-full transition-colors duration-300'
                    >
                      View Full Profile
                    </Link>
                  </div>
                </motion.div>
                <motion.div 
                  className='md:w-1/2'
                  initial="hidden"
                  animate="visible"
                  variants={fadeInRight}
                >
                  <h1 className='text-4xl font-bold mb-2'>
                    {highlightedMember.name}
                  </h1>
                  <p className='text-primary-DEFAULT text-xl mb-4'>
                    {highlightedMember.title}
                  </p>

                  <div className='mb-6'>
                    <h2 className='text-2xl font-bold mb-2'>
                      Highlight Description
                    </h2>
                    <p>
                      {highlightedMember.description ||
                        highlightedMember.shortBio ||
                        "This week we're featuring the incredible " +
                          highlightedMember.name +
                          '!'}
                    </p>
                  </div>

                  <div className='mb-6'>
                    <h2 className='text-2xl font-bold mb-4'>
                      {highlightedMember.name} Quote
                    </h2>
                    <p className='italic'>
                      {highlightedMember.quote ||
                        'Join me on this incredible journey through the digital realm!'}
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default WeeklyHighlight;
